import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useNavigation } from '../../contexts/NavigationContext';
import { useClickOutside } from '../../hooks/useClickOutside';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { getCurrentUserCredits } from '../../services/CreditsService';
import { createAuthRequestInit } from '../../utils/auth';
import { API_BASE } from '../../config/api';

interface UserNotification {
  id: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  type?: 'success' | 'failure' | 'info';
  action?: string;
}

const TopBar: React.FC = () => {
  const { user, logout } = useNavigation();
  const navigate = useNavigate();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isDocDropdownOpen, setIsDocDropdownOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [remainingCredits, setRemainingCredits] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const notificationsRef = useRef<HTMLDivElement>(null);
  const docDropdownRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  
  useClickOutside(notificationsRef, () => setIsNotificationsOpen(false));
  useClickOutside(docDropdownRef, () => setIsDocDropdownOpen(false));
  useClickOutside(profileRef, () => setIsProfileOpen(false));

  // Track if this is the initial load
  const isInitialLoadRef = React.useRef(true);

  // Fetch user credits function
  const fetchCredits = React.useCallback(async () => {
    if (!user) {
      setRemainingCredits(null);
      setIsLoadingCredits(false);
      return;
    }

    try {
      // Only show loading spinner on initial load to avoid flickering during auto-refresh
      if (isInitialLoadRef.current) {
        setIsLoadingCredits(true);
      }
      const credits = await getCurrentUserCredits();
      setRemainingCredits(credits.remaining);
      isInitialLoadRef.current = false;
    } catch (error) {
      console.error('Failed to fetch user credits:', error);
      // Don't reset to null on error during auto-refresh, keep last known value
      if (isInitialLoadRef.current) {
        setRemainingCredits(null);
        isInitialLoadRef.current = false;
      }
    } finally {
      setIsLoadingCredits(false);
    }
  }, [user]);

  // Initial fetch and when user changes
  useEffect(() => {
    isInitialLoadRef.current = true;
    fetchCredits();
  }, [user, fetchCredits]);

  // Auto-refresh credits periodically (every 30 seconds)
  useEffect(() => {
    if (!user) return;

    const intervalId = setInterval(() => {
      fetchCredits();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [user, fetchCredits]);

  // Refresh credits when window regains focus (user switches back to tab)
  useEffect(() => {
    if (!user) return;

    const handleFocus = () => {
      fetchCredits();
      fetchNotifications();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, fetchCredits]);

  // Fetch user notifications
  const fetchNotifications = React.useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    try {
      setIsLoadingNotifications(true);
      const res = await fetch(`${API_BASE}/notifications/user`, createAuthRequestInit());
      
      if (res.ok) {
        const json = await res.json();
        const userNotifications = json.data?.notifications || json.notifications || [];
        setNotifications(userNotifications);
        const unread = userNotifications.filter((n: UserNotification) => !n.read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [user]);

  // Initial fetch and when user changes
  useEffect(() => {
    fetchNotifications();
  }, [user, fetchNotifications]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!user) return;

    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(intervalId);
  }, [user, fetchNotifications]);

  // Mark notification as read
  const markAsRead = React.useCallback(async (notificationId: string) => {
    try {
      const res = await fetch(`${API_BASE}/notifications/${notificationId}/read`, createAuthRequestInit({
        method: 'POST'
      }, true));
      
      if (res.ok) {
        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const res = await fetch(`${API_BASE}/notifications/read-all`, createAuthRequestInit({
        method: 'POST'
      }, true));
      
      if (res.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // When notifications dropdown opens, mark visible notifications as read
  useEffect(() => {
    if (isNotificationsOpen && notifications.length > 0) {
      // Mark unread notifications as read after a short delay
      const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
      if (unreadIds.length > 0) {
        const timer = setTimeout(() => {
          unreadIds.forEach(id => markAsRead(id));
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [isNotificationsOpen, notifications, markAsRead]);

  return (
    <div className="bg-white border-b">
      <div className="px-6 py-4 flex items-center">
        {/* Left - Empty now that Dashboard title is removed */}
        <div></div>

        {/* Middle - CTA Buttons */}
        <div className="flex-1 flex items-center justify-center space-x-4">
          <button 
            className="border-2 border-[#FF5722] text-[#FF5722] px-4 py-1.5 rounded-md hover:bg-[#FF5722] hover:text-white transition-colors duration-200"
          >
            Add Funds
          </button>
          <Link 
            to="/packages"
            className="border-2 border-[#FF5722] text-[#FF5722] px-4 py-1.5 rounded-md hover:bg-[#FF5722] hover:text-white transition-colors duration-200"
          >
            Packages
          </Link>
        </div>

        {/* Right - Controls */}
        <div className="flex items-center space-x-4">
          {/* <div
            className="px-4 py-2 bg-gray-200 text-gray-500 text-sm rounded-md cursor-not-allowed"
          >
            Refer a Friend (Unavailable)
          </div> */}

          <div className="flex flex-col items-center">
            <span className="font-normal">
              {isLoadingCredits ? (
                <span className="text-gray-400">...</span>
              ) : remainingCredits !== null ? (
                remainingCredits.toLocaleString()
              ) : (
                <span className="text-gray-400">0</span>
              )}
            </span>
            <span className="text-sm font-bold">Credits</span>
          </div>
          
          {/* Feedback Button */}
          {/* <button 
            className="hover:bg-gray-100 p-1 rounded-full transition-colors"
            aria-label="Feedback"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          </button> */}

          {/* Documentation Dropdown */}
          <div className="relative" ref={docDropdownRef}>
            <Link 
              to="/documentation" 
              className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
            >
              <BookOpenIcon className="w-6 h-6" />
              <span className="sr-only">Documentation</span>
            </Link>
            
            {isDocDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200">
                <Link to="/docs" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Documentation
                </Link>
                <Link to="/api-docs" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  API Reference
                </Link>
              </div>
            )}
          </div>

          {/* Notification Bell */}
          <div className="relative" ref={notificationsRef}>
            <button 
              className="p-1 hover:bg-gray-100 rounded-full transition-colors relative"
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {/* Badge showing unread notification count */}
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold text-white bg-red-500 rounded-full ring-2 ring-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {isNotificationsOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Notifications</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-xs text-[#FF5722] hover:text-[#F4511E] font-medium"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {isLoadingNotifications ? (
                    <div className="px-4 py-8 text-center">
                      <p className="text-sm text-gray-500">Loading notifications...</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg mb-4">
                        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-600">No notifications</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${
                            !notification.read ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => {
                            if (!notification.read) {
                              markAsRead(notification.id);
                            }
                            setIsNotificationsOpen(false);
                            navigate('/settings/notifications');
                          }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-0.5">
                              {notification.type === 'success' ? (
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              ) : notification.type === 'failure' ? (
                                <div className="w-2 h-2 rounded-full bg-red-500"></div>
                              ) : (
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className={`text-sm font-medium ${
                                  !notification.read ? 'text-gray-900' : 'text-gray-700'
                                }`}>
                                  {notification.title}
                                </p>
                                {!notification.read && (
                                  <span className="ml-2 flex-shrink-0 w-2 h-2 bg-[#FF5722] rounded-full"></span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {notification.message}
                              </p>
                              {notification.action && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Action: {notification.action}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(notification.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Profile Dropdown */}
          <div className="relative" ref={profileRef}>
            <button 
              className="flex items-center space-x-3 hover:bg-gray-100 rounded-full transition-colors p-1"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
            >
              {/* Profile Avatar */}
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-600">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            </button>

            {isProfileOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200">
                {/* User Info */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  {/* User Account */}
                  <Link to="/settings/account" className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    User Profile
                  </Link>

                  {/* Settings disabled */}
                  <div className="flex items-center px-4 py-2 text-sm text-gray-400 cursor-not-allowed">
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings unavailable
                  </div>

                  {/* Logout */}
                  <div className="border-t border-gray-100">
                    <button onClick={() => { setIsProfileOpen(false); logout(); }} className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar; 